import { AlertCircle, CheckCircle2, XCircle, Info } from 'lucide-react'
import { Modal } from './Modal'

export type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm'

export interface AlertModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  type: AlertType
  title: string
  message: string
  confirmText?: string
  cancelText?: string
}

export function AlertModal({
  isOpen,
  onClose,
  onConfirm,
  type,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}: AlertModalProps) {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle2 size={24} color="var(--color-lime-green)" />
      case 'error':
        return <XCircle size={24} color="var(--color-coral-red)" />
      case 'warning':
        return <AlertCircle size={24} color="var(--color-amber-500)" />
      case 'info':
      case 'confirm':
        return <Info size={24} color="var(--color-sky-blue)" />
      default:
        return <Info size={24} color="var(--color-sky-blue)" />
    }
  }

  const getConfirmButtonColor = () => {
    switch (type) {
      case 'success':
        return 'var(--color-lime-green)'
      case 'error':
        return 'var(--color-coral-red)'
      case 'warning':
        return 'var(--color-amber-500)'
      case 'info':
      case 'confirm':
        return 'var(--color-sky-blue)'
      default:
        return 'var(--color-sky-blue)'
    }
  }

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    }
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      maxWidth="500px"
    >
      <div style={{ padding: '0' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: type === 'success' 
              ? 'rgba(152, 202, 63, 0.1)' 
              : type === 'error'
              ? 'rgba(255, 76, 91, 0.1)'
              : type === 'warning'
              ? 'rgba(255, 193, 7, 0.1)'
              : 'rgba(0, 174, 239, 0.1)'
          }}>
            {getIcon()}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: 0, 
              marginBottom: '0.5rem',
              fontSize: 'var(--text-lg)', 
              fontWeight: 'var(--font-weight-semibold)', 
              color: 'var(--color-gray-900)' 
            }}>
              {title}
            </h3>
            <p style={{ 
              margin: 0,
              fontSize: 'var(--text-base)', 
              color: 'var(--color-gray-600)', 
              lineHeight: 'var(--leading-relaxed)' 
            }}>
              {message}
            </p>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          justifyContent: 'flex-end',
          marginTop: '1.5rem'
        }}>
          {(type === 'confirm' || type === 'warning') && (
            <button
              onClick={onClose}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: 'var(--color-gray-200)',
                color: 'var(--color-gray-700)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'all var(--default-transition-duration) var(--ease-in-out)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-gray-300)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-gray-200)'
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={type === 'confirm' || type === 'warning' ? handleConfirm : onClose}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: getConfirmButtonColor(),
              color: 'var(--color-pure-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              transition: 'all var(--default-transition-duration) var(--ease-in-out)',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            {type === 'confirm' || type === 'warning' ? confirmText : 'OK'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

